import {
  FilterOptionEntry,
  HospitalizationsSliceState,
} from '../types/hospitalizations.types';
import {
  GoalsTrackingViewDto,
  FilterDto,
  HospitalizationDto,
} from '@act/shared/data-transfer-objects';
import {
  EntityId,
  PayloadAction,
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { feedSlice, getFeedItems } from 'redux/slices/feedSlice';
import { resetRedux } from '../actions/shared-actions';
import { getGoalsForMember } from 'redux/slices/goalsTracking.slice';
import type { WebsocketEventActions } from 'redux/slices/websocketSlice';
import { useAppSelector } from 'redux/store-hooks';
import { createApiThunk } from 'redux/utils/api-thunk';
import { hospitalizationsRequests } from 'redux/requests/hospitalizations.requests';
import { HospitalizationEventTypes } from '@act/shared/socket-events';

// THUNKS/ACTIONS ==============================================================
export const getHospitalizationFilterOptions = createApiThunk(
  hospitalizationsRequests.getFilterOptions
);

export const getCurrentAndPendingHospitalizations = createApiThunk(
  hospitalizationsRequests.getCurrentAndPendingHospitalizations
);

export const updateHospitalizationSuccess = createAction<HospitalizationDto>(
  'hospitalizations/updateHospitalizationSuccess'
);

// INITIAL STATE ===============================================================
const hospitalizationsAdapter = createEntityAdapter<HospitalizationDto>();
const filterOptionsAdapter = createEntityAdapter<FilterDto, EntityId>({
  selectId: filter => filter.key,
  sortComparer: (a, b) => a.key.localeCompare(b.key),
});

const initialState: HospitalizationsSliceState = {
  status: 'initial',
  filterOptionsStatus: 'initial',
  currentAndPendingStatus: 'initial',
  hospitalizations: hospitalizationsAdapter.getInitialState(),
  filterOptions:
    filterOptionsAdapter.getInitialState() as unknown as FilterOptionEntry,
  currentAndPendingMemberMap: {},
  currentAndPendingMeta: {
    total: 0,
    offeredCount: 0,
    attemptedCount: 0,
  },
};

// SLICE =======================================================================
export const hospitalizationsSlice = createSlice({
  name: 'hospitalizations',
  initialState,
  reducers: {
    gotHospitalizations: (state, action) => {
      hospitalizationsAdapter.addMany(
        state.hospitalizations,
        action.payload.data
      );
      state.status = 'success';

      return state;
    },
    hospitalizationUpdated: (state, action) => {
      state.hospitalizations = hospitalizationsAdapter.upsertOne(
        state.hospitalizations,
        action.payload.hospitalization
      );

      return state;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, () => {
        const newState = { ...initialState };
        return newState;
      })
      .addCase(feedSlice.actions.feedItemUpdated, (state, action) => {
        if (!action.payload.feedItemModelView.hospitalizations) return;
        hospitalizationsAdapter.addMany(
          state.hospitalizations,
          action.payload.feedItemModelView.hospitalizations
        );
        state.status = 'success';
      })
      .addCase(getFeedItems.fulfilled, (state, action) => {
        hospitalizationsAdapter.addMany(
          state.hospitalizations,
          action.payload.hospitalizations
        );
        state.status = 'success';
      })
      .addCase(getHospitalizationFilterOptions.fulfilled, (state, action) => {
        filterOptionsAdapter.addMany(state.filterOptions, action.payload);
        state.filterOptionsStatus = 'success';
      })
      .addCase(getHospitalizationFilterOptions.pending, state => {
        state.filterOptionsStatus = 'loading';
      })
      .addCase(getHospitalizationFilterOptions.rejected, state => {
        state.filterOptionsStatus = 'failed';
      })
      .addCase(getCurrentAndPendingHospitalizations.pending, state => {
        state.currentAndPendingStatus = 'loading';
      })
      .addCase(
        getCurrentAndPendingHospitalizations.fulfilled,
        (state, action) => {
          // Store all hospitalizations (including completed ones) in entity adapter
          hospitalizationsAdapter.upsertMany(
            state.hospitalizations,
            action.payload.allHospitalizations
          );
          // Store object directly (only current/pending for display)
          state.currentAndPendingMemberMap = action.payload.data;
          state.currentAndPendingMeta = action.payload.meta;
          state.currentAndPendingStatus = 'success';
        }
      )
      .addCase(getCurrentAndPendingHospitalizations.rejected, state => {
        state.currentAndPendingStatus = 'failed';
      })
      .addCase(
        getGoalsForMember.fulfilled,
        (state, action: PayloadAction<GoalsTrackingViewDto>) => {
          const { assignedSurveys } = action.payload;
          const hospitalizations = assignedSurveys
            .filter(survey => survey.hospitalizations)
            .map(survey => survey.hospitalizations);

          hospitalizationsAdapter.addMany(
            state.hospitalizations,
            hospitalizations.flat()
          );
        }
      )
      .addCase(updateHospitalizationSuccess, (state, action) => {
        hospitalizationsAdapter.upsertOne(
          state.hospitalizations,
          action.payload
        );
      });
  },
});

// REDUCER =====================================================================
export const hospitalizationsReducer = hospitalizationsSlice.reducer;

// ACTIONS =====================================================================
export const hospitalizationsActions = hospitalizationsSlice.actions;

// SELECTORS ===================================================================

const selectHospitalizationsSliceState = state =>
  (state as { hospitalizations: typeof initialState })?.hospitalizations;

export const selectHospitalizations = state =>
  selectHospitalizationsSliceState(state).hospitalizations;

// select all hospitalizations
export const selectAllHospitalizations = createSelector(
  selectHospitalizations,
  hospitalizations =>
    hospitalizations.ids.map(id => hospitalizations.entities[id])
);

const selectHospitalizationById = (id: number) =>
  createSelector(
    selectHospitalizations,
    hospitalizations => hospitalizations.entities[id]
  );

const selectHospitalizationsStatus = state =>
  selectHospitalizationsSliceState(state).status;

const selectHospitalizationFilterOptions = state =>
  selectHospitalizationsSliceState(state).filterOptions;

// select all filter options
const selectAllFilterOptions = createSelector(
  selectHospitalizationFilterOptions,
  filterOptions => filterOptions.ids.map(id => filterOptions.entities[id])
);

const selectHospitalizationFilterOptionsStatus = state =>
  selectHospitalizationsSliceState(state).filterOptionsStatus;

const selectCurrentAndPendingStatus = state =>
  selectHospitalizationsSliceState(state).currentAndPendingStatus;

const selectCurrentAndPendingMemberMap = state =>
  selectHospitalizationsSliceState(state).currentAndPendingMemberMap;

// Select current and pending hospitalizations grouped by member
const selectCurrentAndPendingHospitalizationsGrouped = createSelector(
  [selectHospitalizations, selectCurrentAndPendingMemberMap],
  (hospitalizations, memberMap) => {
    const groupedData = new Map<string, HospitalizationDto[]>();

    for (const [memberId, hospitalizationIds] of Object.entries(memberMap)) {
      const memberHospitalizations = hospitalizationIds
        .map(id => hospitalizations.entities[id])
        .filter((h): h is HospitalizationDto => h !== undefined);

      if (memberHospitalizations.length > 0) {
        groupedData.set(memberId, memberHospitalizations);
      }
    }

    return groupedData;
  }
);

// EXPORT HELPER ===============================================================
export const hospitalizationsSliceSelectors = {
  useSelectHospitalizationById: (id: number) =>
    useAppSelector(selectHospitalizationById(id)),
  useSelectHospitalizations: () => useAppSelector(selectAllHospitalizations),
  useSelectHospitalizationsStatus: () =>
    useAppSelector(selectHospitalizationsStatus),
  useSelectAllHospitalizationFilterOptions: () =>
    useAppSelector(selectAllFilterOptions),
  useSelectHospitalizationFilterOptionsStatus: () =>
    useAppSelector(selectHospitalizationFilterOptionsStatus),
  useSelectCurrentAndPendingStatus: () =>
    useAppSelector(selectCurrentAndPendingStatus),
  useSelectCurrentAndPendingHospitalizationsGrouped: () =>
    useAppSelector(selectCurrentAndPendingHospitalizationsGrouped),
};

// EFFECTS =====================================================================

// WEBSOCKET ACTIONS ===========================================================
export const hospitalizationsSliceWebsocketActions: WebsocketEventActions[] = [
  {
    type: HospitalizationEventTypes.HOSPITALIZATION_UPDATED,
    action: hospitalizationsSlice.actions.hospitalizationUpdated,
    channel: 'baseSocket',
  },
];
